import { useCallback, useEffect, useState } from 'react';
import { Canvas } from 'fabric';
import { usePieceDispatch } from '../../model/piece-context';
import { useSelectedLayerId } from '../../selection/hooks/use-selected-layer';
import { HighlightRange } from '../utils/text-highlights';
import { selectionRange, writeSelectionStyle, type TextObject } from '../utils/selection-style';
import { useOnSelectionChange } from './use-on-selection-change';

// TODO(brand-kit): sourced from the brand kit once the endpoint exists.
const HIGHLIGHT_COLOR = '#DD3330';

const isBlockHighlighted = (t: TextObject, [start, end]: [number, number]): boolean => {
  const styles = t.getSelectionStyles(start, end, true);
  return styles.length > 0 && styles.every(s => s.textBackgroundColor === HIGHLIGHT_COLOR);
};

const carveOut = (ranges: HighlightRange[], start: number, end: number): HighlightRange[] =>
  ranges.flatMap(r => {
    if (r.end <= start || r.start >= end) return [r];
    const parts: HighlightRange[] = [];
    if (r.start < start) parts.push({ ...r, end: start });
    if (r.end > end) parts.push({ ...r, start: end });
    return parts;
  });

/** The two highlight buttons. `*Active` is ON when a click will remove. */
export function useTextHighlights(textObject: TextObject | null, canvas: Canvas | null) {
  const dispatch = usePieceDispatch();
  const layerId = useSelectedLayerId();
  const [blockActive, setBlockActive] = useState(false);
  const [tightActive, setTightActive] = useState(false);

  const syncStates = useCallback(() => {
    const range = textObject && selectionRange(textObject);
    if (!textObject || !range) {
      setBlockActive(false);
      setTightActive(false);
      return;
    }
    const [start, end] = range;
    setBlockActive(isBlockHighlighted(textObject, range));
    setTightActive((textObject.highlightRanges ?? []).some(r => r.start < end && r.end > start));
  }, [textObject]);

  useOnSelectionChange(textObject, syncStates);

  // Typing shifts char indices, so the ranges go stale.
  useEffect(() => {
    if (!textObject || !layerId) return;
    const drop = () => {
      if (!textObject.isEditing || !textObject.highlightRanges?.length) return;
      textObject.highlightRanges = undefined;
      textObject.dirty = true;
      canvas?.requestRenderAll();
      dispatch({ type: 'patchLayerContent', id: layerId, patch: { highlightRanges: undefined } });
    };
    textObject.on('changed', drop);
    return () => textObject.off('changed', drop);
  }, [textObject, canvas, layerId, dispatch]);

  const toggleBlock = useCallback(() => {
    if (!textObject || !canvas || !layerId) return;
    const range = selectionRange(textObject);
    if (!range) return;

    const color = isBlockHighlighted(textObject, range) ? '' : HIGHLIGHT_COLOR;
    const styles = writeSelectionStyle(textObject, 'textBackgroundColor', color);
    canvas.requestRenderAll();
    dispatch({ type: 'patchLayerContent', id: layerId, patch: { styles } });
    syncStates();
  }, [textObject, canvas, layerId, dispatch, syncStates]);

  const toggleTight = useCallback(() => {
    if (!textObject || !canvas || !layerId) return;
    const range = selectionRange(textObject);
    if (!range) return;

    const [start, end] = range;
    const ranges = textObject.highlightRanges ?? [];
    const overlaps = ranges.some(r => r.start < end && r.end > start);
    const next = overlaps ? carveOut(ranges, start, end) : [...ranges, { start, end, color: HIGHLIGHT_COLOR }];
    const highlightRanges = next.length > 0 ? next : undefined;

    textObject.highlightRanges = highlightRanges; // preview
    textObject.dirty = true; // not a Fabric cacheProperty
    canvas.requestRenderAll();
    dispatch({ type: 'patchLayerContent', id: layerId, patch: { highlightRanges } });
    syncStates();
  }, [textObject, canvas, layerId, dispatch, syncStates]);

  return { blockActive, tightActive, toggleBlock, toggleTight };
}
